import React, { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import { AutoComplete } from "antd";

import { BACKEND_CONFIG } from "../../config/consts";

import "antd/dist/antd.min.css";

const MIN_INPUT_LENGTH = 3;

const Typeahead = ({
  "data-testid": dataTestId,
  onSelect,
  onClientChange,
  ...props
}) => {
  const [autocompleteOptions, setAutocompleteOptions] = useState(null);
  const [autocompleteValue, setAutocompleteValue] = useState("");
  const [isMonitoringEnabled, setIsMonitoringEnabled] = useState(true);
  const [isAutocompleteDropdownOpen, setIsAutocompleteDropdownOpen] = useState(false);

  const autocompleteRef = useRef();
  const clientIdRef = useRef("");

  const brandCode = BACKEND_CONFIG.get("brandCode");
  const busUnitId = BACKEND_CONFIG.get("busUnitId");

  const handleNonMonitoredSearch = useCallback((value) => {
    axios
      .request({
        url: `webcategoriestypeahead/${brandCode}/${busUnitId}/search?query=${encodeURIComponent(
          value
        )}`,
        method: "get",
        baseURL: "/api/primary_contentmanagement/",
      })
      .then((response) => {
        setAutocompleteOptions(
          response.data.map(({ Id, Description }) => ({
            label: (
              <span data-testid="typeahead-text-label">{Description}</span>
            ),
            value: `${Id}|${Description}`,
          }))
        );
      });
  }, [brandCode, busUnitId]);

  const handleMonitoredSearch = useCallback((value) => {
    axios
      .request({
        url: `typeahead/webbusclasses/${busUnitId}/${brandCode}?query=${encodeURIComponent(
          value
        )}${clientIdRef.current ? `&clientId=${clientIdRef.current}` : ""}`,
        method: "get",
        baseURL: "/api/primary_ua/",
      })
      .then((response) => {
        setAutocompleteOptions(
          response.data.TypeAheadResults.map(({ Id, Description }) => ({
            label: (
              <span data-testid="typeahead-text-label">{Description}</span>
            ),
            value: `${Id}|${Description}`,
          }))
        );

        if (clientIdRef.current !== response.data.ClientId) {
          clientIdRef.current = response.data.ClientId;
          onClientChange?.(response.data.ClientId);
        }
      })
      .catch(() => {
        setIsMonitoringEnabled(false);

        handleNonMonitoredSearch(value);
      });
  }, [brandCode, busUnitId, handleNonMonitoredSearch, onClientChange]);

  const handleSearch = useCallback(debounce((value) => {
    if (value.length >= MIN_INPUT_LENGTH) {
      if (isMonitoringEnabled) {
        handleMonitoredSearch(value);
      } else {
        handleNonMonitoredSearch(value);
      }
    } else if (value.length > 0) {
      setAutocompleteOptions(null);
    }
  }, 500), [handleMonitoredSearch, handleNonMonitoredSearch, isMonitoringEnabled]);

  const handleChange = useCallback((value) => {
    setAutocompleteValue(value);
    setIsAutocompleteDropdownOpen(true);
  }, []);

  const handleSelect = useCallback((value, { label }) => {
    onSelect(value);

    setAutocompleteValue(label.props.children);

    setTimeout(() => {
      setIsAutocompleteDropdownOpen(false);
    });
  }, [onSelect]);

  const handleClick = useCallback(() => {
    setIsAutocompleteDropdownOpen(true);
  }, []);

  useEffect(() => {
    clientIdRef.current = "";
  }, []);

  return (
    <div>
      <AutoComplete
        autoFocus
        ref={autocompleteRef}
        options={autocompleteOptions}
        value={autocompleteValue}
        open={isAutocompleteDropdownOpen}
        style={{ display: "block", height: 50 }}
        notFoundContent={autocompleteOptions?.length === 0 ? "No matches found" : undefined}
        getPopupContainer={trigger => trigger.parentElement}
        onChange={handleChange}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClick={handleClick}
        {...props}
      >
        <input
          data-testid={dataTestId}
          className="form-control"
          placeholder="Search for a business type by description or NAICS code"
        />
      </AutoComplete>
    </div>
  );
};

export default Typeahead;
